import { useEffect, useRef } from "react";
import type { AppRouterContext } from "~/lib/router-runtime";

/** One early normal read per ready session; Query owns its data and lifetime. */
export function AppAccountingPrewarmer({
  context,
}: {
  context: AppRouterContext;
}) {
  const started = useRef<{
    queryClient: AppRouterContext["queryClient"];
    sessionScopeToken: string;
  } | null>(null);

  useEffect(() => {
    if (!context.auth.authenticated) {
      started.current = null;
      return;
    }
    if (
      !context.auth.ready ||
      !context.auth.vmPreferenceLoaded ||
      !context.auth.primaryWalletAddress?.trim() ||
      !context.accountingPlatformApi
    )
      return;
    const sessionScopeToken = context.auth.sessionQueryScope.scopeToken;
    if (
      started.current?.queryClient === context.queryClient &&
      started.current.sessionScopeToken === sessionScopeToken
    )
      return;
    let active = true;
    void import("~/features/dashboard/dashboard-route")
      .then(({ prewarmDashboardAccountSummary }) => {
        // Auth may change while the optional chunk loads. Never enqueue an
        // old-scope request after the auth boundary has already removed it.
        if (!active) return;
        started.current = {
          queryClient: context.queryClient,
          sessionScopeToken,
        };
        prewarmDashboardAccountSummary(context);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [context]);

  return null;
}
